<template>
  <div v-if="hasOrgItemLoaded">
    <ParagraphBody>
      Device Certificates allow authentication mechanisms to recognize if
      login requests are coming from JumpCloud managed devices.
      <Link
        v-if="includeKbLink"
        :href="kbArticlesUrls.deviceCertPolicyLink"
        :isExternal="true"
        target="_blank"
        text="Learn more about device certificates."
      />
    </ParagraphBody>
    <ul :class="$style.list">
      <li>
        To use device-based conditional authentication policies,
        the devices need to have a JumpCloud certificate installed on them.
      </li>
      <li>
        The JumpCloud certificate is only used by authentication policies.
      </li>
      <li>
        Some browsers may prompt users to select the certificate on their next visit
        to the JumpCloud User Portal. Users need to confirm certificate selection
        or restart their browser for the certificate to take effect. We recommend
        that you notify your users about this before you turn on certificate distribution.
      </li>
    </ul>
    <div>
      <PrettyToggle
        ref="toggle"
        :config="{ value: enabled }"
        :handleToggleChange="handleToggleChange"
        label="Global Certificate Distribution"
      />
    </div>
    <ParagraphBody>
      Turning off certificate distribution will remove previously distributed certificates
      and revoke macOS keychain application access.
    </ParagraphBody>
    <AppAuthorizationSection
      v-if="showAppAuthorizationSection"
      :deviceCertEnabled="enabled"
    />
  </div>
</template>

<script>
import AppAuthorizationSection from 'client/Security/AppAuthorization/AppAuthorizationSection';
import AuthNPoliciesCertificateSettingsMixin from 'client/Security/mixins/AuthNPoliciesCertificateSettingsMixin';
import DisableDeviceCertificateDistributionConfirmationModal
  from 'client/Security/DisableDeviceCertificateDistributionConfirmationModal.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import PrettyToggle from 'client/components/FormValidation/PrettyToggle.vue';

import {
  Link,
  ParagraphBody,
} from '@jumpcloud/ui-components';

function getEnabled(item) {
  if (item.settings) {
    return item.settings.deviceIdentificationEnabled;
  }
  return false;
}

export default {
  name: 'CertificateSettings',
  ModalEventBus,
  components: {
    AppAuthorizationSection,
    Link,
    ParagraphBody,
    PrettyToggle,
  },

  mixins: [AuthNPoliciesCertificateSettingsMixin],

  props: {
    includeKbLink: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({ hasOrgItemLoaded: false }),

  computed: {
    enabled() {
      return getEnabled(this.authNPoliciesCertItem);
    },
    kbArticlesUrls() {
      return {
        deviceCertPolicyLink: KbArticleUrls.conditionalPolicyDeviceCertificates,
      };
    },
    // don't show app authorization section when certificate settings are in a modal
    showAppAuthorizationSection() {
      return this.includeKbLink;
    },
  },

  async mounted() {
    await this.initializeCertificateSettings();

    // trusted apps are initialized here for an edge case when loading conditional policies settings
    // with global device cert enabled and an empty trusted app list, so that we can pre-load
    // default jumpcloud trusted apps
    if (this.showAppAuthorizationSection && this.enabled) {
      await this.initializeTrustedAppsSettings();
    }
    this.hasOrgItemLoaded = true;
  },

  methods: {
    handleToggleChange() {
      if (!this.enabled || !getEnabled(this.authNPoliciesCertPreviousItem)) {
        this.toggleDeviceIdentification();
        return;
      }

      this.$options.ModalEventBus.open({
        isLargeModal: false,
        component: DisableDeviceCertificateDistributionConfirmationModal,
        componentProps: {
          action: this.toggleDeviceIdentification,
        },
      });
    },

    toggleDeviceIdentification() {
      this.authNPoliciesCertSetItemField({
        key: 'settings',
        value: {
          ...this.authNPoliciesCertItem.settings,
          deviceIdentificationEnabled: !this.enabled,
        },
      });
    },
  },
};
</script>

<style module>
  .note.note {
    font-weight: 700;
    margin-bottom: 20px;
    padding-left: 20px;
  }

  .list.list {
    margin-bottom: 15px;
    margin-top: 20px;
    padding-inline-start: 20px;
  }

  .list li:not(:last-child) {
    margin-bottom: 10px;
  }
</style>
